import React, { useState } from "react";
import {
  Dialog,
  IconButton,
  Button,
  TextField,
  Box,
  Typography,
  makeStyles,
  Theme,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import RichTextEditor from "react-rte";
import { toast } from "react-toastify";
import { useQueryClient } from "react-query";

import { NoteTypeSection } from "./NoteTypeSection";
import { OwnerSection } from "./OwnerSection";
import { TaggedUsersSection } from "./TaggedUsersSection";
import { LabelsSection } from "./LabelsSection";
import { DispositionSection } from "./DispositionSection";

import { processErrorOnMutation } from "../../../../../util/errorMessaging";
import { useFetchContext } from "../../../../common/FetchContext";
import { useAuthContext } from "../../../../common/AuthContext";
import {
  useLinkFileToNote,
  useSubmitNote,
} from "../../../../common/CommonMutations";
import { usePostFile } from "../../../../common/services/fileServices";
import { FileSection } from "./FileSection";

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    borderRadius: 12,
    overflow: "hidden",
    maxHeight: "90vh",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: "1.1rem",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  contentGrid: {
    display: "flex",
    flexDirection: "row",
    minHeight: 520,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  leftColumn: {
    flex: 1,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflowY: "auto",
  },
  rightColumn: {
    width: 320,
    backgroundColor: "#f9f9fb",
    borderLeft: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    overflowY: "auto",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      borderLeft: "none",
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  subjectInput: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
  footerActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  createBtn: {
    backgroundColor: "#e08c00",
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    padding: "6px 20px",
    borderRadius: 6,
    "&:hover": {
      backgroundColor: "#c77c00",
    },
  },
  closeBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 6,
    padding: "6px 20px",
    color: "#555",
  },
  sidebarLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#888",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
}));

export const CreateNoteModal = ({
  id,
  model,
  open,
  onClose,
  invalidate,
  onSuccess,
}: any) => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const { authAxios } = useFetchContext();
  const auth = useAuthContext();

  const { mutateAsync: mutateSubmitNote } = useSubmitNote();
  const { mutateAsync: mutatePostFile } = usePostFile();
  const { mutateAsync: mutateLinkFile } = useLinkFileToNote();

  // Form & Metadata State
  const [subject, setSubject] = useState("");
  const [subjectError, setSubjectError] = useState(false);
  const [text, setText] = useState(RichTextEditor.createEmptyValue());
  const [selectedNoteType, setSelectedNoteType] = useState<number>(1);
  const [disposition, setDisposition] = useState({ name: "Available", id: 16 });
  const [usedLabels, setUsedLabels] = useState<any[]>([]);
  const [owner, setOwner] = useState({
    username: auth?.state?.user || "",
    id: Number(auth?.state?.id) || -1,
  });
  const [taggedUsers, setTaggedUsers] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => {
    setSubject("");
    setSubjectError(false);
    setText(RichTextEditor.createEmptyValue());
    setSelectedNoteType(1);
    setDisposition({ name: "Available", id: 16 });
    setUsedLabels([]);
    setOwner({
      username: auth?.state?.user || "",
      id: Number(auth?.state?.id) || -1,
    });
    setTaggedUsers([]);
    setFiles([]);
  };

  const handleCreate = async () => {
    if (!subject.trim()) {
      setSubjectError(true);
      return;
    }

    setIsLoading(true);
    try {
      const newLabels = usedLabels.map((label) => label.id);
      const alertUsers = taggedUsers.map((user) => user.id);

      const newNote = await mutateSubmitNote({
        authAxios,
        id,
        model,
        subject,
        text: text.toString("markdown"),
        note_type: selectedNoteType,
        owner: owner.id,
        disposition: disposition.id,
        labels: newLabels,
        groups: [],
        tagged_users: alertUsers,
      });

      // Handle attached files
      for (const file of files) {
        const newFile = await mutatePostFile({ authAxios, file });
        await mutateLinkFile({
          authAxios,
          fileId: newFile.id,
          noteId: newNote.id,
        });
      }
      // const newNote = {
      //   id,
      //   model,
      //   subject,
      //   text: text.toString("markdown"),
      //   note_type: selectedNoteType,
      //   owner: owner.id,
      //   disposition: disposition.id,
      //   labels: newLabels,
      //   groups: [],
      //   tagged_users: alertUsers,
      // };
      console.log("newNote ", newNote);

      // Handle query invalidations
      if (invalidate && Array.isArray(invalidate)) {
        invalidate.forEach((key) => queryClient.invalidateQueries(key));
      }
      queryClient.invalidateQueries("tasks");
      queryClient.invalidateQueries("notes");
      queryClient.invalidateQueries("closedTasks");
      queryClient.invalidateQueries("closedFlags");

      toast.success("Note created successfully.");
      if (onSuccess) onSuccess(newNote);

      resetForm();
      onClose();
    } catch (err) {
      toast.error("Failed to create note.");
      processErrorOnMutation(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      {/* Header Bar */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.headerTitle}>Create New Note</Typography>
        <Box className={classes.headerRight}>
          {isLoading && (
            <CircularProgress size={20} style={{ marginRight: 8 }} />
          )}
          <IconButton size="small" onClick={onClose} disabled={isLoading}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Main Container */}
      <Box className={classes.contentGrid}>
        {/* Left Column (Subject input, Rich Text Body, Actions) */}
        <Box className={classes.leftColumn}>
          <Box>
            <TextField
              fullWidth
              variant="outlined"
              label="Subject"
              placeholder="Enter note subject..."
              value={subject}
              error={subjectError}
              helperText={subjectError ? "Subject is required" : ""}
              onChange={(e) => {
                setSubject(e.target.value);
                if (e.target.value.trim()) setSubjectError(false);
              }}
              className={classes.subjectInput}
            />

            {/* Rich Text Editor */}
            <RichTextEditor
              value={text}
              onChange={setText}
              toolbarConfig={{
                display: [
                  "INLINE_STYLE_BUTTONS",
                  "BLOCK_TYPE_BUTTONS",
                  "BLOCK_TYPE_DROPDOWN",
                  "HISTORY_BUTTONS",
                ],
                INLINE_STYLE_BUTTONS: [
                  { label: "Bold", style: "BOLD" },
                  { label: "Italic", style: "ITALIC" },
                  { label: "Underline", style: "UNDERLINE" },
                  { label: "Strikethrough", style: "STRIKETHROUGH" },
                ],
                BLOCK_TYPE_DROPDOWN: [
                  { label: "Normal", style: "unstyled" },
                  { label: "Heading Large", style: "header-one" },
                  { label: "Heading Medium", style: "header-two" },
                ],
                BLOCK_TYPE_BUTTONS: [
                  { label: "UL", style: "unordered-list-item" },
                  { label: "OL", style: "ordered-list-item" },
                  { label: "Blockquote", style: "blockquote" },
                ],
                BLOCK_ALIGNMENT_BUTTONS: [], // Added to satisfy TypeScript requirement
              }}
            />
          </Box>

          {/* Footer Bar */}
          <Box className={classes.footerActions}>
            <Button
              variant="outlined"
              onClick={onClose}
              className={classes.closeBtn}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              startIcon={<SendIcon style={{ fontSize: 16 }} />}
              onClick={handleCreate}
              className={classes.createBtn}
              disabled={isLoading}
            >
              Create Note
            </Button>
          </Box>
        </Box>

        {/* Right Sidebar (Metadata Controls) */}
        <Box className={classes.rightColumn}>
          {/* Type Of Note */}
          <Box>
            <Typography className={classes.sidebarLabel}>
              TYPE OF NOTE
            </Typography>
            <NoteTypeSection
              noteType={selectedNoteType}
              setNoteType={setSelectedNoteType}
            />
          </Box>

          {/* Owner Section */}
          <Box>
            <Typography className={classes.sidebarLabel}>OWNER</Typography>
            <OwnerSection owner={owner} setOwner={setOwner} isNew={true} />
          </Box>

          {/* Tagged Users Section */}
          <Box>
            <Typography className={classes.sidebarLabel}>
              TAGGED USERS
            </Typography>
            <TaggedUsersSection
              taggedUsers={taggedUsers}
              setTaggedUsers={setTaggedUsers}
              isNew={true}
            />
          </Box>

          {/* Labels Section */}
          <Box>
            <Typography className={classes.sidebarLabel}>LABELS</Typography>
            <LabelsSection
              usedLabels={usedLabels}
              setUsedLabels={setUsedLabels}
              isNew={true}
            />
          </Box>

          {/* Status / Disposition Section */}
          <Box>
            <Typography className={classes.sidebarLabel}>STATUS</Typography>
            <DispositionSection
              disposition={disposition}
              setDisposition={setDisposition}
              isNew={true}
            />
          </Box>
          <Box>
            <Typography className={classes.sidebarLabel}>
              ATTACHMENTS
            </Typography>
            <FileSection files={files} setFiles={setFiles} isNew={true} />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};
